import { CheckCircleIcon } from "@chakra-ui/icons";
import { Box, Flex, Heading, Text } from "@chakra-ui/layout";
import {
  Alert,
  Button,
  ButtonGroup,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import { useFormikContext } from "formik";
import React, { useCallback, useState } from "react";
import useConfirmation from "../../hooks/useConfirmation";
import useQuote from "../../hooks/useQuote";
import useUser from "../../hooks/useUser";
import { darkMode, lightMode } from "../../lib/models/color-mode-colors";
import { FormStatusColorMap } from "../../lib/models/form-status-colors";
import { FixAndFlipQuote } from "../../lib/models/short-term/short-term.model";
import { PdfGenerationActions } from "../../lib/types/PDFGenerationActions";
import { QuoteFormActions } from "../../lib/types/QuoteFormActions";
import { isElevatedUser } from "../../lib/UserPermissions";
import DeleteQuoteDialog from "../DeleteQuoteDialog";
import { Calculator } from "../icons/Calculator";
import DocumentReportIcon from "../icons/DocumentReportIcon";
import DotsVerticalIcon from "../icons/DotsVerticalIcon";
import { WarningTriangleOutline } from "../icons/WarningTriangleOutline";
import ShowForRole from "../ShowForRole";

export type FormHeadingProps = {
  label: string;
  handler: (action: QuoteFormActions, values: any) => void;
  exportHandler: (action: PdfGenerationActions, values: any) => void;
  generatingPdf?: boolean;
  calculating?: boolean;
  children?: any;
};

function FormHeading({
  label,
  children,
  calculating,
  generatingPdf,
  handler,
  exportHandler,
}: FormHeadingProps) {
  const quoteContext = useQuote();
  const { role } = useUser();
  const { values, dirty, isValid, touched, setFieldValue } =
    useFormikContext<any>();
  const quoteHandler = useCallback(
    (action: QuoteFormActions) => {
      handler(action, values);
    },
    [handler, values]
  );
  const { dialogOpen, loading, toggleOpen, performAction } = useConfirmation(
    () => quoteHandler("DELETE")
  );
  const { state, dispatch, isOwned } = quoteContext;
  const { metadata, quote } = state;
  const { yellow, brand, yellowDark, brandDark } = FormStatusColorMap;
  const { colorMode } = useColorMode();
  const [touchedLength, setTouchedLength] = useState<number>(0);

  const numberOfTouchedFields = Object.keys(touched).length;
  const bgColor = useColorModeValue(lightMode[100], darkMode[200]);

  const resetCalculationStatus = React.useCallback(() => {
    dispatch({
      type: "QUOTE_CALCULATION_RESET",
    });
  }, [dispatch]);

  const updateFormStatus = React.useCallback(
    (dirty: boolean) => {
      dispatch({
        type: "QUOTE_STATUS_UPDATED",
        payload: dirty,
      });
    },
    [dispatch]
  );

  const calcQuote = () => {
    setTouchedLength(numberOfTouchedFields);
    quoteHandler("CALCULATE");
    if (quote?.quoteType === "STF") {
      const stfQuote = quote as FixAndFlipQuote;
      setFieldValue("borrowerTier", stfQuote.borrowerInfo?.borrowerTier);
    }
  };

  const handleGeneratePdfClick = (action: PdfGenerationActions) => {
    exportHandler(action, values);
  };

  const commonDisable =
    !isValid ||
    !state.hasCalculated ||
    state.hasCalculationErrors ||
    state.requiresQuoteResubmission ||
    state.hasFormErrors;

  const disablePdf = () => {
    if (state.quoteType === "Rental")
      return (
        !state.rentalQuoteScenarios.some((x) => x.included === "Yes") ||
        commonDisable ||
        state.isNewQuote ||
        dirty
      );
    return commonDisable || state.isNewQuote || dirty;
  };

  React.useEffect(() => {
    updateFormStatus(dirty);
  }, [dirty, updateFormStatus]);

  React.useEffect(() => {
    if (dirty) {
      resetCalculationStatus();
    }
    if (touchedLength !== numberOfTouchedFields) {
      resetCalculationStatus();
    }
  }, [dirty, resetCalculationStatus, numberOfTouchedFields, touchedLength]);

  return (
    <Box
      as="section"
      paddingTop={6}
      paddingBottom={{ base: 6, lg: 0 }}
      paddingX={{ base: 4, md: 8, lg: 12 }}
      top="0"
      left={0}
      position={{ base: "initial", "2xl": "sticky" }}
      zIndex={10}
      backgroundColor={bgColor}
      borderTopRadius={6}
      borderBottomRadius={{ base: 6, lg: 0 }}
    >
      <DeleteQuoteDialog
        isOpen={dialogOpen}
        isQuoteDeleting={loading}
        onConfirmHandler={performAction}
        onCloseHandler={toggleOpen}
        quoteToDelete={null}
      />
      <Flex flexWrap="wrap" alignItems="center" justifyContent="space-between">
        <Box width={"full"}>
          <Flex
            alignItems="center"
            justifyContent="space-between"
            wrap="wrap"
            gridGap={6}
            minWidth={0}
          >
            <Heading
              fontWeight="900"
              size="lg"
              as="h2"
              maxWidth={575}
              isTruncated={true}
              title={label}
            >
              {label}
            </Heading>
            <Box display={{ base: "none", lg: "flex" }}>
              <Button
                leftIcon={<Calculator opacity={0.7} h={6} w={6} />}
                onClick={() => calcQuote()}
                isDisabled={!isValid || state.hasFormErrors}
                isLoading={calculating}
                loadingText="Calculating.."
                mr={4}
              >
                <Text fontSize="sm" flex="1">
                  Calculate
                </Text>
              </Button>

              <ShowForRole roles={["External"]} hideForAdmin>
                <Button
                  leftIcon={<DocumentReportIcon opacity={0.7} h={6} w={6} />}
                  isLoading={generatingPdf}
                  onClick={() => {
                    role !== "External"
                      ? handleGeneratePdfClick("CUSTOMER")
                      : handleGeneratePdfClick("WHITELABEL");
                  }}
                  loadingText="Generating.."
                  variant="solid"
                  isDisabled={disablePdf()}
                  mr={4}
                >
                  <Text fontSize="sm" flex="1">
                    Generate PDF
                  </Text>
                </Button>
              </ShowForRole>

              <ShowForRole
                roles={[
                  "Sales",
                  "Broker",
                  "Broker Manager",
                  "Lender Finance",
                  "Lender Finance Manager",
                  "Wholesale",
                  "Wholesale Manager",
                  "Capital Markets",
                  "Inside Sales",
                  "Inside Sales Manager",
                  "Business Development",
                  "Business Development Manager",
                  "POD",
                  "POD Manager",
                  "Production Coordinator",
                  "Operations Analyst",
                  "Sales Assistant",
                  "Administrator",
                ]}
              >
                <Menu placement="bottom-end">
                  <ButtonGroup isAttached>
                    <Button
                      leftIcon={
                        <DocumentReportIcon opacity={0.7} h={6} w={6} />
                      }
                      isLoading={generatingPdf}
                      onClick={() => {
                        role !== "External"
                          ? handleGeneratePdfClick("CUSTOMER")
                          : handleGeneratePdfClick("WHITELABEL");
                      }}
                      loadingText="Generating.."
                      variant="solid"
                      isDisabled={disablePdf()}
                    >
                      <Text fontSize="sm" flex="1">
                        Generate PDF
                      </Text>
                    </Button>
                    <MenuButton
                      as={IconButton}
                      title="More PDF Generation Options"
                      aria-label="More PDF Generation Options"
                      icon={<DotsVerticalIcon h={6} w={6} />}
                      isDisabled={disablePdf() || generatingPdf}
                      mr={4}
                      borderLeft="1px"
                      borderLeftColor="gray.300"
                    />
                  </ButtonGroup>

                  <MenuList>
                    <MenuItem
                      onClick={() => handleGeneratePdfClick("CUSTOMER")}
                    >
                      Customer PDF
                    </MenuItem>

                    <MenuItem
                      onClick={() => handleGeneratePdfClick("INTERNAL")}
                    >
                      Internal PDF
                    </MenuItem>

                    <MenuItem
                      onClick={() => handleGeneratePdfClick("WHITELABEL")}
                    >
                      Whitelabel PDF
                    </MenuItem>
                  </MenuList>
                </Menu>
              </ShowForRole>

              <Box display={{ base: "none", md: "block" }}>
                <Button
                  w="full"
                  variant="brand"
                  isDisabled={
                    commonDisable ||
                    (!isOwned && !isElevatedUser(role)) ||
                    metadata?.underwriterStatus === "Sales Finalized" ||
                    metadata?.underwriterStatus === "Final"
                  }
                  onClick={() => quoteHandler("SAVE")}
                >
                  Save Quote
                </Button>
              </Box>
            </Box>
          </Flex>
        </Box>
        {!isOwned && !isElevatedUser(role) && (
          <Alert
            status="warning"
            justifyContent="center"
            fontSize={14}
            paddingY={2}
            marginTop={6}
            style={colorMode === "light" ? yellow : yellowDark}
          >
            <Text
              color={
                colorMode === "light" ? yellow.textColor : yellowDark.textColor
              }
            >
              <WarningTriangleOutline marginRight={4} />
              This quote is read-only. You are not the owner, any changes you
              make will not be saved.
            </Text>
          </Alert>
        )}
        {(metadata?.underwriterStatus === "Sales Finalized" ||
          metadata?.underwriterStatus === "Final") && (
          <Alert
            status="success"
            justifyContent="center"
            fontSize={14}
            paddingY={2}
            marginTop={6}
            style={colorMode === "light" ? brand : brandDark}
          >
            <Text
              color={
                colorMode === "light" ? brand.textColor : brandDark.textColor
              }
            >
              <CheckCircleIcon marginRight={4} />
              This quote is read-only. The underwriting process has been
              completed, any changes you make will not be saved.
            </Text>
          </Alert>
        )}
        {metadata?.underwriterStatus === "Under Review" && (
          <Alert
            status="warning"
            justifyContent="center"
            fontSize={14}
            paddingY={2}
            marginTop={6}
            style={colorMode === "light" ? yellow : yellowDark}
          >
            <Text
              color={
                colorMode === "light" ? yellow.textColor : yellowDark.textColor
              }
            >
              <WarningTriangleOutline marginRight={4} />
              This quote has been submitted to the underwriting team. Only the
              available fields will be editable.
            </Text>
          </Alert>
        )}
      </Flex>
      {children}
    </Box>
  );
}

export default FormHeading;
